import React, { useState, useEffect } from 'react';
import { Header, Footer } from './components';

// Free Assessment Page
export const FreeAssessmentPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [step, setStep] = useState('initial'); // 'initial', 'assessment', 'complete'
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    teamSize: '',
    challenges: '',
    goals: ''
  });
  
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Psychometric questions based on Five Voices model (reworded to be original)
  const assessmentQuestions = [
    {
      id: 1,
      question: "When facing a complex business challenge, what's your natural first instinct?",
      options: [
        { value: "pioneer", text: "Look at the big picture and envision future possibilities" },
        { value: "connector", text: "Consider how this affects the people involved" },
        { value: "creative", text: "Think of innovative, unconventional solutions" },
        { value: "guardian", text: "Analyze the details and potential risks" },
        { value: "nurturer", text: "Focus on maintaining team harmony and support" }
      ]
    },
    {
      id: 2,
      question: "In team meetings, you're most likely to:",
      options: [
        { value: "pioneer", text: "Drive the agenda toward strategic outcomes" },
        { value: "connector", text: "Ensure everyone's voice is heard" },
        { value: "creative", text: "Suggest alternative approaches others haven't considered" },
        { value: "guardian", text: "Ask detailed questions about implementation" },
        { value: "nurturer", text: "Help resolve conflicts and maintain positive dynamics" }
      ]
    },
    {
      id: 3,
      question: "When making important decisions, you typically:",
      options: [
        { value: "pioneer", text: "Trust your instincts and move quickly" },
        { value: "connector", text: "Seek input from key stakeholders" },
        { value: "creative", text: "Explore multiple options before deciding" },
        { value: "guardian", text: "Gather comprehensive data and analyze thoroughly" },
        { value: "nurturer", text: "Consider the emotional impact on everyone affected" }
      ]
    },
    {
      id: 4,
      question: "Your ideal work environment is:",
      options: [
        { value: "pioneer", text: "Fast-paced with autonomy to drive results" },
        { value: "connector", text: "Collaborative with strong relationships" },
        { value: "creative", text: "Flexible with freedom to experiment" },
        { value: "guardian", text: "Structured with clear processes and expectations" },
        { value: "nurturer", text: "Supportive with emphasis on team wellbeing" }
      ]
    },
    {
      id: 5,
      question: "When projects aren't going well, you tend to:",
      options: [
        { value: "pioneer", text: "Take charge and redirect toward the vision" },
        { value: "connector", text: "Facilitate discussions to find common ground" },
        { value: "creative", text: "Suggest completely new approaches" },
        { value: "guardian", text: "Identify what went wrong and create prevention plans" },
        { value: "nurturer", text: "Support struggling team members" }
      ]
    },
    {
      id: 6,
      question: "Your communication style is best described as:",
      options: [
        { value: "pioneer", text: "Direct and results-focused" },
        { value: "connector", text: "Warm and relationship-building" },
        { value: "creative", text: "Inspiring and possibility-oriented" },
        { value: "guardian", text: "Detailed and factual" },
        { value: "nurturer", text: "Encouraging and supportive" }
      ]
    },
    {
      id: 7,
      question: "When learning new skills, you prefer to:",
      options: [
        { value: "pioneer", text: "Jump in and learn through action" },
        { value: "connector", text: "Learn alongside others in a group setting" },
        { value: "creative", text: "Explore unconventional learning methods" },
        { value: "guardian", text: "Follow structured, step-by-step approaches" },
        { value: "nurturer", text: "Learn in a supportive, low-pressure environment" }
      ]
    },
    {
      id: 8,
      question: "Your biggest strength in a team is:",
      options: [
        { value: "pioneer", text: "Driving toward ambitious goals" },
        { value: "connector", text: "Building bridges between different personalities" },
        { value: "creative", text: "Generating innovative ideas and solutions" },
        { value: "guardian", text: "Ensuring quality and attention to detail" },
        { value: "nurturer", text: "Creating a positive, supportive atmosphere" }
      ]
    },
    {
      id: 9,
      question: "Under pressure, you:",
      options: [
        { value: "pioneer", text: "Become more focused and decisive" },
        { value: "connector", text: "Rally the team together" },
        { value: "creative", text: "Find unexpected solutions" },
        { value: "guardian", text: "Become more systematic and careful" },
        { value: "nurturer", text: "Focus on keeping everyone calm and supported" }
      ]
    },
    {
      id: 10,
      question: "Your approach to change is:",
      options: [
        { value: "pioneer", text: "Embrace it as an opportunity" },
        { value: "connector", text: "Help others navigate it together" },
        { value: "creative", text: "See it as a chance to innovate" },
        { value: "guardian", text: "Carefully plan and manage the transition" },
        { value: "nurturer", text: "Support those who struggle with change" }
      ]
    },
    {
      id: 11,
      question: "When giving feedback, you:",
      options: [
        { value: "pioneer", text: "Are direct about what needs to improve" },
        { value: "connector", text: "Focus on maintaining the relationship" },
        { value: "creative", text: "Suggest new perspectives and possibilities" },
        { value: "guardian", text: "Provide specific, detailed observations" },
        { value: "nurturer", text: "Emphasize encouragement and growth" }
      ]
    },
    {
      id: 12,
      question: "Your motivation comes from:",
      options: [
        { value: "pioneer", text: "Achieving significant results and impact" },
        { value: "connector", text: "Building meaningful relationships" },
        { value: "creative", text: "Solving complex problems creatively" },
        { value: "guardian", text: "Maintaining excellence and standards" },
        { value: "nurturer", text: "Helping others succeed and feel valued" }
      ]
    },
    {
      id: 13,
      question: "In conflict situations, you typically:",
      options: [
        { value: "pioneer", text: "Address it head-on to resolve quickly" },
        { value: "connector", text: "Mediate to find win-win solutions" },
        { value: "creative", text: "Reframe the situation from new angles" },
        { value: "guardian", text: "Focus on facts and proper procedures" },
        { value: "nurturer", text: "Prioritize healing relationships" }
      ]
    },
    {
      id: 14,
      question: "Your energy is highest when:",
      options: [
        { value: "pioneer", text: "Driving breakthrough results" },
        { value: "connector", text: "Facilitating great team interactions" },
        { value: "creative", text: "Working on innovative projects" },
        { value: "guardian", text: "Completing tasks to high standards" },
        { value: "nurturer", text: "Supporting and developing others" }
      ]
    },
    {
      id: 15,
      question: "Your ideal role would involve:",
      options: [
        { value: "pioneer", text: "Leading strategic initiatives" },
        { value: "connector", text: "Building partnerships and networks" },
        { value: "creative", text: "Designing new solutions and systems" },
        { value: "guardian", text: "Ensuring quality and compliance" },
        { value: "nurturer", text: "Developing and mentoring people" }
      ]
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Just move to assessment phase - we'll submit everything at the end
      setStep('assessment');
    } catch (error) {
      console.error('Error starting assessment:', error);
      alert('There was an error starting your assessment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssessmentAnswer = (questionId, answer) => {
    setAssessmentAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    
    if (currentQuestion < assessmentQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // Complete assessment
      completeAssessment(answer);
    }
  };

  const completeAssessment = async (finalAnswer) => {
    const finalAnswers = { ...assessmentAnswers, [assessmentQuestions[currentQuestion].id]: finalAnswer };
    
    try {
      // Submit complete assessment to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submit-assessment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          team_size: formData.teamSize,
          challenges: formData.challenges,
          goals: formData.goals,
          assessment_answers: finalAnswers
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit assessment');
      }

      const result = await response.json();
      console.log('Assessment completed successfully:', result);
      
      setStep('complete');
    } catch (error) {
      console.error('Error completing assessment:', error);
      alert('There was an error submitting your assessment. Please try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (step === 'complete') {
    return (
      <div className="min-h-screen">
        <Header />
        
        <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white rounded-lg shadow-lg p-12">
              <div className="text-6xl mb-6">✨</div>
              <h1 className="text-3xl font-serif font-bold text-gray-900 mb-6">
                Assessment Complete!
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Thank you, {formData.name}! Your personalized flow profile has been created and Craig has been notified.
              </p>
              <div className="bg-teal-50 p-6 rounded-lg mb-8">
                <h3 className="font-semibold text-gray-900 mb-2">What happens next:</h3>
                <ul className="text-left text-gray-700 space-y-2">
                  <li>• You'll receive an email confirmation shortly</li>
                  <li>• Craig will review your personalized flow profile</li>
                  <li>• He'll contact you within 24 hours to discuss your results</li>
                  <li>• You'll receive specific recommendations for accessing flow state</li>
                </ul>
              </div>
              <a 
                href="https://app.usemotion.com/meet/craigwilmann/meeting" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-teal-600 text-white px-8 py-3 rounded-md font-medium hover:bg-teal-700 transition-colors inline-block"
              >
                Or Book a Call Now
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  if (step === 'assessment') {
    const question = assessmentQuestions[currentQuestion];
    const progress = ((currentQuestion + 1) / assessmentQuestions.length) * 100;
    
    return (
      <div className="min-h-screen">
        <Header />
        
        <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Question {currentQuestion + 1} of {assessmentQuestions.length}</span>
                <span>{Math.round(progress)}% Complete</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-8 text-center">
                {question.question}
              </h2>
              
              <div className="space-y-4">
                {question.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAssessmentAnswer(question.id, option.value)}
                    className="w-full p-4 text-left border border-gray-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <span className="text-gray-900">{option.text}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  // Initial form (step === 'initial')
  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
            Free flow assessment
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Discover your unique flow triggers and get personalized recommendations for peak performance
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6">
              Tell us about your business
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Size
                  </label>
                  <select
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="">Select team size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-25">11-25 employees</option>
                    <option value="26-50">26-50 employees</option>
                    <option value="51-100">51-100 employees</option>
                    <option value="100+">100+ employees</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What are your biggest challenges right now?
                </label>
                <textarea
                  name="challenges"
                  rows={4}
                  value={formData.challenges}
                  onChange={handleChange}
                  placeholder="e.g., Team needs constant oversight, low productivity, high turnover..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What would success look like for your business?
                </label>
                <textarea
                  name="goals"
                  rows={4}
                  value={formData.goals}
                  onChange={handleChange}
                  placeholder="e.g., Self-managing teams, higher engagement, sustainable growth..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 text-white px-8 py-3 rounded-md font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Starting Assessment...' : 'Start Assessment'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Workshops Page
export const WorkshopsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const workshops = [
    {
      title: "Time Management & Flow State",
      duration: "Half Day (4 hours)",
      participants: "Whole teams- up to 50 people",
      description: "Transform how your team manages time and accesses flow state for peak performance.",
      outcomes: [
        "Master effective time blocking techniques",
        "Uncover personalised flow triggers",
        "Implement the 'not to do' list",
        "Build transparency systems",
        "Unlock lasting change in your business"
      ],
      price: "£1,750+"
    },
    {
      title: "Creative Mindset Training",
      duration: "Full Day (8 hours)",
      participants: "Individuals (5-15 people)",
      description: "Transfrom your mindset to allow for flow and enhanced idea generation.",
      outcomes: [
        "Advanced flow state techniques",
        "Understand the link between flow and creativity",
        "Strategic thinking in flow",
        "Unlock the secret source behind all great ideas",
        "Lasting mindset transformation"
      ],
      price: "£450pp"
    },
    {
      title: "AI Integration for Flow",
      duration: "Whole teams- up to 50 people",
      participants: "Up to 15 people",
      description: "Learn how to use AI tools to free up time and mental space for flow state work.",
      outcomes: [
        "AI tools for time management",
        "Automation strategies",
        "Reducing cognitive load",
        "AI-assisted decision making",
        "Creating space for deep work"
      ],
      price: "£1,750+"
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
            Flow state workshops
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Practical, hands-on workshops that deliver immediate results for your team
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12">
            {workshops.map((workshop, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 border border-gray-200">
                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                      {workshop.title}
                    </h2>
                    <p className="text-lg text-gray-600 mb-6">
                      {workshop.description}
                    </p>
                    
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      What you'll achieve:
                    </h3>
                    <ul className="space-y-2">
                      {workshop.outcomes.map((outcome, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-teal-500 mr-2">✓</span>
                          <span className="text-gray-700">{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="lg:col-span-1">
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <div className="mb-4">
                        <span className="text-sm font-medium text-gray-500">Duration</span>
                        <p className="text-lg font-semibold text-gray-900">{workshop.duration}</p>
                      </div>
                      <div className="mb-4">
                        <span className="text-sm font-medium text-gray-500">Participants</span>
                        <p className="text-lg font-semibold text-gray-900">{workshop.participants}</p>
                      </div>
                      <div className="mb-6">
                        <span className="text-sm font-medium text-gray-500">Investment</span>
                        <p className="text-2xl font-bold text-teal-600">{workshop.price}</p>
                      </div>
                      <a 
                        href="/workshop-booking" 
                        className="w-full bg-teal-600 text-white px-6 py-3 rounded-md font-medium hover:bg-teal-700 transition-colors block text-center"
                      >
                        Book This Workshop
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Podcast Page
export const PodcastPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const episodes = [
    {
      title: "What Was the Point?",
      description: "The final episode of Meaningless Problems - a thought-provoking conclusion to the series",
      duration: "15 min",
      date: "Feb 2024"
    },
    {
      title: "Instant Connect (performed by Taj Atwal)",
      description: "A dating app that implants love stories into users' memories - performed by actress Taj Atwal",
      duration: "18 min",
      date: "Feb 2024"
    },
    {
      title: "The Loop",
      description: "An exploration of reincarnation and the concept of consciousness cycling through time",
      duration: "12 min", 
      date: "Jan 2024"
    },
    {
      title: "My Home is a Prison",
      description: "The inaugural episode that launched Meaningless Problems - a story about confinement and freedom",
      duration: "14 min",
      date: "Mar 2023"
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
            Meaningless Problems Podcast
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            I have a short story podcast where I release original stories across various genres, exploring themes such as consciousness, free will and AI. And what's that got to do with anything? Well...
          </p>
        </div>
      </section>

      {/* About the Podcast */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6">
            About Meaningless Problems
          </h2>
          <p className="text-lg text-gray-600 mb-6">
            Meaningless Problems is a collection of original short stories created and written by me (under my pseudonym Doe Wilmann). Some are comedies. Some are dramas. Some are rubbish.
            But this podcast simply wouldn't be possible without my ability to access the flow state. It is precisely because I wanted to write every day, while having a full time job and a family, that I needed to find a way to work effectively and for it not to seem like a massive drag.  
          </p>
          <p className="text-lg text-gray-600 mb-8">
            Enter the flow state. I can now wake up every single morning knowing that I will be able to access the flow state and write at least two thousand words - and knowing that this energy will carry through for the rest of the day.
            If you get any enjoyment from these stories whatsoever, you have the flow state to thank.  
          </p>
        </div>
      </section>

      {/* Embedded Player */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-8 text-center">
            Listen to all episodes
          </h2>
          
          <div className="bg-white rounded-lg shadow-lg p-6">
            <iframe 
              title="Meaningless Problems" 
              allowtransparency="true" 
              height="315" 
              width="100%" 
              style={{border: 'none', minWidth: 'min(100%, 430px)', height: '315px'}} 
              scrolling="no" 
              data-name="pb-iframe-player" 
              src="https://www.podbean.com/player-v2/?i=2ahcd-f14fbc-pbblog-playlist&share=1&download=1&rtl=0&fonts=Arial&skin=1&font-color=auto&logo_link=episode_page&order=episodic&limit=10&filter=all&ss=a713390a017602015775e868a2cf26b0&btn-skin=7&size=315" 
              loading="lazy" 
              allowfullscreen=""
            ></iframe>
          </div>
          
          <p className="text-center text-gray-600 mt-4">
            Full playlist with all episodes from the complete series
          </p>
        </div>
      </section>

      {/* Featured Episodes */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-12 text-center">
            Featured episodes
          </h2>
          
          <div className="space-y-6">
            {episodes.map((episode, index) => (
              <div key={index} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow border border-gray-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {episode.title}
                    </h3>
                    <p className="text-gray-600 mb-3">
                      {episode.description}
                    </p>
                    <div className="flex items-center text-sm text-gray-500">
                      <span>{episode.duration}</span>
                      <span className="mx-2">•</span>
                      <span>{episode.date}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <p className="text-gray-600 mb-4">
              Plus many more stories exploring consciousness, AI, love, loss, and the meaning of existence
            </p>
          </div>
        </div>
      </section>

      {/* Connection to Love Your Work */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6">
            The connection to Love Your Work
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Meaningless Problems showcases Craig's storytelling background and creative thinking - 
            the same narrative skills he brings to business transformation. Just as each story explores 
            human behavior and motivation, Love Your Work helps you understand what truly drives people at work.
          </p>
          <a 
            href="https://app.usemotion.com/meet/craigwilmann/meeting" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-teal-600 text-white px-8 py-3 rounded-md font-medium hover:bg-teal-700 transition-colors inline-block"
          >
            Work with Craig
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Resources Page - Coming Soon
export const ResourcesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
            Resources coming soon
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            We're preparing valuable resources to help you unlock flow in your business
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-lg shadow-lg p-12">
            <div className="text-6xl mb-6">🚧</div>
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">
              Coming Soon
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              We're working on creating amazing resources including guides, templates, 
              assessments, and tools to help you access flow state.
            </p>
            <p className="text-gray-600 mb-8">
              In the meantime, why not book a free assessment or explore our workshops?
            </p>
            <div className="flex justify-center space-x-4">
              <a href="/free-assessment" className="bg-teal-600 text-white px-6 py-3 rounded-md font-medium hover:bg-teal-700 transition-colors">
                Free Assessment
              </a>
              <a href="/workshops" className="bg-gray-200 text-gray-800 px-6 py-3 rounded-md font-medium hover:bg-gray-300 transition-colors">
                View Workshops
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// About Page
export const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
            About Craig Wilmann
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            From burnout to breakthrough: the story behind Love Your Work
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="https://i.imgur.com/BMw8hy2.jpeg" 
                alt="Craig Wilmann facilitating a business workshop"
                className="w-full h-auto rounded-lg shadow-xl"
              />
              <p className="text-sm text-gray-500 text-center mt-3 italic">Craig facilitating a workshop with business leaders</p>
            </div>
            <div>
              <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6">
                Multiple identities, one mission
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                I'm Craig Wilmann, founder of Love Your Work. I'm also a playwright and short story writer 
                (under the name Doe Wilmann), a father (under the name 'Daddy'), and a business consultant, 
                facilitator and mentor (under the name… well, actually, they just call me Craig).
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                In my mid-twenties, while building a twenty-person, £4 million+ turnover business, 
                I experienced the crippling effects of burnout. It was the worst and best thing that 
                ever happened to me.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Today, my biggest passion in life, aside from writing and looking after my daughter, is helping 
                people to love their work. Not simply to be better at it, or become less affected by it, 
                but to genuinely and wholeheartedly love it.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Fun Boy Three and Bananarama had it right… 'It ain't what you do, it's the way that you do it.'
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Process Details Page
export const ProcessDetailsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const programPillars = [
    {
      number: "1",
      title: "Your Work",
      subtitle: "Non-negotiable time to work on your priorities",
      duration: "6 facilitated 2 hour blocks",
      description: "A commitment to yourself and your business to boost your own effectiveness and wellbeing.",
      details: [
        "While running a business, it’s so difficult to find uninterrupted time for deep work. That’s why these training sessions are locked into your diary: two hours where you are completely uninterruptible unless there’s a fire.",
        "In each session, the expert facilitator will ask for your goals and will help guide you into the flow state. You will spend the next ninety minutes working at your best and feeling great in the process. Afterwards you’ll reflect on what worked well and what can be improved, and you’ll set a goal for the week ahead.",
        "The more you do this, the easier it will become to access the flow state, unlocking exponential benefits for productivity and wellbeing that will stretch far beyond the 2-hour session. Regular exposure to flow is proven to boost creativity and mood, generating energy rather than zapping it. And if your colleagues know you’re doing it, they’ll be much more likely to do it themselves."
      ],
      outcome: "Enhanced personal effectiveness, clearer strategic thinking, sustainable high performance and improved wellbeing"
    },
    {
      number: "2",
      title: "Your Team",
      subtitle: "Team training and ongoing support", 
      duration: "Half-day + ongoing",
      description: "A mindset shift across your whole business to make you work better, do better and feel better.",
      details: [
        "The reality is that most people aren’t that interested in boosting productivity. It sounds stressful and onerous, a benefit to the business rather than themselves. That’s why this comprehensive workshop, led by Love Your Work founder Craig Wilmann, explores the link between deep work, flow state, creativity and wellbeing.",
        "Your colleagues will put this into practice on the day by completing 45 minutes of deep work on an outstanding task. And their feedback will help us tailor the ongoing guidance you will receive to help them perform and feel better.",
        "Once you’ve proven the link between deep work and wellbeing, the personal benefit to each of your colleagues will be obvious. Like you, they’ll find their work energising rather than draining and the benefits will be exponential."
      ],
      outcome: "A team that works more effectively, feels more engaged, and experiences greater job satisfaction"
    },
    {
      number: "3",
      title: "Your Brand",
      subtitle: "Recognition and celebration", 
      duration: "Ongoing",
      description: "A public acknowledgment of your commitment as an employer to helping your colleagues love their work.",
      details: [
        "When you make great strides as a business, you’re often too busy to tell the world about it. That’s why we will document your progress every step of the way.",
        "We’ll announce your involvement in the Love Your Work programme, we’ll provide a digital badge underlining your commitment and we’ll interview your staff to create professional video testimonials.",
        "By promoting you on LinkedIn and creating content you can use on your website, we will send a signal to potential future employees that your organisation is a great place to work."
      ],
      outcome: "Enhanced brand, more attractive to potential employees, and deserved recognition"
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
            The Love Your Work Programme
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Lead by example to boost engagement and enjoyment across your business
          </p>
        </div>
      </section>

      {/* Programme Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-8">
            Because work should be energising not draining
          </h2>
          <p className="text-lg text-gray-600 mb-12 leading-relaxed">
            You want to build a great working environment where colleagues will stay and grow with you. But chances are, you’ve been looking in the wrong place. Perks such as socials, flexible working, and pool tables in the office are great. But what if people could love the work itself?
          </p>
        </div>
      </section>

      {/* Detailed Pillars */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {programPillars.map((pillar, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8">
                <div className="grid lg:grid-cols-3 gap-8">
                  {/* Pillar Number and Title */}
                  <div className="lg:col-span-1">
                    <div className="flex items-center mb-4">
                      <div className="bg-teal-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mr-4">
                        {pillar.number}
                      </div>
                      <div>
                        <h3 className="text-2xl font-serif font-bold text-gray-900">{pillar.title}</h3>
                        <p className="text-gray-600 font-medium">{pillar.subtitle}</p>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <span className="text-sm font-medium text-gray-500">Commitment</span>
                      <p className="text-lg font-semibold text-gray-900">{pillar.duration}</p>
                    </div>
                  </div>

                  {/* Description and Details */}
                  <div className="lg:col-span-2">
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                      {pillar.description}
                    </p>
                    
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">How & why it works:</h4>
                    <ul className="space-y-2 mb-6">
                      {pillar.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-teal-500 mr-2 mt-1">•</span>
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-500">
                      <h5 className="font-semibold text-gray-900 mb-2">Outcome:</h5>
                      <p className="text-gray-700">{pillar.outcome}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-8">
            The transformation you can expect
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="text-left">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">For you as a leader:</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-teal-500 mr-2">✓</span>
                  <span className="text-gray-700">Guaranteed time for deep work and deep thinking</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-500 mr-2">✓</span>
                  <span className="text-gray-700">Greater personal effectiveness & wellbeing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-500 mr-2">✓</span>
                  <span className="text-gray-700">Enhanced reputation as a people-focussed leader</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-500 mr-2">✓</span>
                  <span className="text-gray-700">Ongoing guidance and advice</span>
                </li>
              </ul>
            </div>
            
            <div className="text-left">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">For your business:</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-teal-500 mr-2">✓</span>
                  <span className="text-gray-700">Higher productivity</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-500 mr-2">✓</span>
                  <span className="text-gray-700">Improved team engagement and wellbeing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-500 mr-2">✓</span>
                  <span className="text-gray-700">Better talent attraction and retention</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-500 mr-2">✓</span>
                  <span className="text-gray-700">Sustainable success without burnout</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gray-50 p-8 rounded-lg">
            <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">
              Ready to join the Love Your Work Programme?
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              Book a call with founder Craig Wilmann to discuss how it can work for your business
            </p>
            <a 
              href="https://app.usemotion.com/meet/craigwilmann/meeting" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-teal-600 text-white px-8 py-3 rounded-md font-medium hover:bg-teal-700 transition-colors inline-block"
            >
              Book Your Discovery Call
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
export const FestivalPage = () => {
  const [timeLeft, setTimeLeft] = useState({});
  const [timer, setTimer] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Set target date to March 3rd, 2026 at 1:30 PM
    const targetDate = new Date('2026-03-03T13:30:00').getTime();
    
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const difference = targetDate - now;
      
      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        };
      } else {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
    };
    
    // Set initial time
    setTimeLeft(calculateTimeLeft());
    
    // Update every second
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    setTimer(interval);
    
    // Cleanup
    return () => {
      if (timer) clearInterval(timer);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl lg:text-7xl font-serif font-bold mb-6 leading-tight">
              The London Business Festival
              <span className="block text-4xl lg:text-5xl text-purple-300 mt-2">2026</span>
            </h1>
            
            <div className="text-xl lg:text-2xl mb-8 space-y-2">
              <p className="text-purple-200">3rd-13th March</p>
              <p className="text-purple-200">The Old Red Lion Theatre, EC17 4NJ</p>
            </div>
            
            {/* Countdown Timer */}
            <div className="mt-8 bg-black bg-opacity-30 backdrop-blur-sm rounded-lg p-6 max-w-4xl mx-auto">
              <h3 className="text-xl font-semibold mb-4 text-purple-200">Festival Starts In:</h3>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-white bg-opacity-10 rounded-lg p-4">
                  <div className="text-3xl lg:text-4xl font-bold text-white">
                    {timeLeft.days || 0}
                  </div>
                  <div className="text-sm lg:text-base text-purple-200 mt-1">Days</div>
                </div>
                <div className="bg-white bg-opacity-10 rounded-lg p-4">
                  <div className="text-3xl lg:text-4xl font-bold text-white">
                    {timeLeft.hours || 0}
                  </div>
                  <div className="text-sm lg:text-base text-purple-200 mt-1">Hours</div>
                </div>
                <div className="bg-white bg-opacity-10 rounded-lg p-4">
                  <div className="text-3xl lg:text-4xl font-bold text-white">
                    {timeLeft.minutes || 0}
                  </div>
                  <div className="text-sm lg:text-base text-purple-200 mt-1">Minutes</div>
                </div>
                <div className="bg-white bg-opacity-10 rounded-lg p-4">
                  <div className="text-3xl lg:text-4xl font-bold text-white">
                    {timeLeft.seconds || 0}
                  </div>
                  <div className="text-sm lg:text-base text-purple-200 mt-1">Seconds</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section One: Entertainment - insight - action */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-8">
            Entertainment - insight - action.
          </h2>
          
          <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
            <p>
              Each day, fifty founders and C-suite leaders will arrive at The Old Red Lion Theatre to take part in a three-step process to transform their business.
            </p>
            
            <p>
              With all the upsides of a huge event combined with the intimacy of an exclusive gathering, The London Business Festival will change the face of live business experiences in this country.
            </p>
            
            <p className="text-xl font-semibold text-purple-900">
              And you can be at the centre of it.
            </p>
          </div>
        </div>
      </section>

      {/* Section Two: What makes it different */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-8">
              What makes it different
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="text-6xl mb-4 text-center">🎯</div>
              <p className="text-gray-700 leading-relaxed">
                A single topic for each day (eg recruitment, future-proofing, personal brand) allows you to ensure you are laser-focused on your top challenge.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="text-6xl mb-4 text-center">👥</div>
              <p className="text-gray-700 leading-relaxed">
                With the guest-list maxed out at 50 per day, founder & C-Suite only, you won't get lost in the crowd. You'll have time to make genuine, lasting connections.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="text-6xl mb-4 text-center">🎭</div>
              <p className="text-gray-700 leading-relaxed">
                Set in a legendary London Theatre Pub, crafted for conversation and storytelling. And the chance for you to attend the premiere of a new play in the evening.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="text-6xl mb-4 text-center">📋</div>
              <p className="text-gray-700 leading-relaxed">
                Each day follows a three step process. Business Confessions, Business Masterclass, Business Action Plan. Hear stories that could sink industries, learn how to do it right, and craft a plan for your business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Three: Get Involved */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-8">
            Get Involved
          </h2>
          
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            If you'd like to attend, sponsor or share your story at the festival, get in touch.
          </p>
          
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-8 rounded-lg shadow-lg">
            <h3 className="text-2xl font-bold text-purple-900 mb-4">
              Ready to join The London Business Festival?
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              The full line-up and ticket details will be announced soon.
            </p>
            <div className="space-y-4 mb-6">
              <p className="text-gray-700">
                <span className="font-semibold">Contact:</span> craig@loveyourwork.uk
              </p>
              <p className="text-gray-700">
                <span className="font-semibold">Subject line:</span> 'Festival'
              </p>
            </div>
            <a 
              href="mailto:craig@loveyourwork.uk?subject=Festival"
              className="inline-block bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-md font-medium hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105"
            >
              Contact Us About The Festival
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export const SupportBritishTheatrePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="wait-with-me">
      <header className="container hero" aria-label="Hero">
        <div className="hero-media" style={{
          background: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url("https://images.unsplash.com/photo-1507924538820-ede94a04019d?q=80&w=2400&auto=format&fit=crop") center/cover no-repeat',
          opacity: 0.6
        }} aria-hidden="true"></div>
        <div className="hero-inner">
          <h1 className="heading-xl">Support British Theatre</h1>
          <p className="sub" style={{fontSize: '1.25rem'}}>Boost your brand with the ultimate in-person experience: exceptional live theatre.</p>
          <div className="cta">
            <a className="btn secondary" href="mailto:craig@loveyourwork.uk?subject=Patron%20enquiry%20—%20While%20We%20Wait&body=Hello,%20I'd%20like%20to%20help%20Support%20British%20Theatre...">Become a Patron</a>
            <a className="btn" href="mailto:craig@loveyourwork.uk?subject=Executive%20Producer%20enquiry%20—%20While%20We%20Wait&body=Hello,%20I'd%20like%20to%20help%20Support%20British%20Theatre...">Become an Executive Producer</a>
          </div>
        </div>
      </header>

      <main className="container grid" aria-label="Main content">
        {/* Mission */}
        <section className="card" id="mission">
          <h2 className="heading-lg">Our Mission</h2>
          <p style={{marginBottom: '24px'}}>We support emerging theatre productions with the potential for West End transfers and world tours. And you make it possible.</p>
          
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
            <div style={{display: 'flex', alignItems: 'center', padding: '12px', backgroundColor: 'rgba(110,231,210,.1)', borderRadius: '8px'}}>
              <span style={{fontSize: '24px', marginRight: '12px'}}>🎁</span>
              <span style={{fontWeight: '600'}}>Entertain your friends & clients</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', padding: '12px', backgroundColor: 'rgba(110,231,210,.1)', borderRadius: '8px'}}>
              <span style={{fontSize: '24px', marginRight: '12px'}}>🏢</span>
              <span style={{fontWeight: '600'}}>Strengthen your brand</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', padding: '12px', backgroundColor: 'rgba(110,231,210,.1)', borderRadius: '8px'}}>
              <span style={{fontSize: '24px', marginRight: '12px'}}>🎭</span>
              <span style={{fontWeight: '600'}}>Secure your legacy</span>
            </div>
          </div>
        </section>

        {/* Our Next Production */}
        <section className="card" id="about">
          <h2 className="heading-lg">Our Next Production: While We Wait</h2>
          
          <p>A man who is experiencing life in slow motion falls for a woman whose life is speeding by. Can their love survive when time is not on their side? <em>While We Wait</em> is a "hilarious and heart‑breaking" new play. 3rd-28th March 2026, The Old Red Lion Theatre, London</p>
          
          <br />
          
          <p><strong>Cast & creatives:</strong> Starring multi-award-winning actress <em>Kirsten Callaghan</em> and acclaimed actor <em>Ricky Oakley</em>. Directed by West End director <em>Scott Le Crass</em>. Written by <em>Doe Wilmann</em>, creator of the <em>Meaningless Problems</em> podcast.</p>
          
          <p><strong>Produced by</strong> <em>Meaningless Problems Presents…</em> under <em>Wait With Me Ltd</em>.</p>
          
          <p><strong>Supported by...</strong> You</p>
        </section>

        {/* Two ways to support */}
        <section className="grid" id="support-explanation">
          <h2 className="heading-lg">Two Ways to Support British Theatre</h2>
          
          <div className="cols-2">
            <article className="card">
              <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                <div style={{backgroundColor: 'var(--brand)', color: '#081019', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', marginRight: '12px'}}>1</div>
                <h3 style={{margin: '0', fontSize: '20px'}}>Become a Patron</h3>
              </div>
              <p style={{marginBottom: '20px', lineHeight: '1.5'}}>An evening will be held in your honour, showcasing you and your business as proud supporters of British theatre. After the final bow, the actors will thank you on stage before joining your guests for a private afterparty. Your name will be forever associated with the play.</p>
              <a href="mailto:craig@loveyourwork.uk?subject=Patron%20enquiry%20—%20While%20We%20Wait&body=Hello,%20I'd%20like%20to%20help%20Support%20British%20Theatre..." className="btn secondary" style={{width: '100%', justifyContent: 'center'}}>Enquire Now</a>
            </article>

            <article className="card">
              <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                <div style={{backgroundColor: 'var(--brand)', color: '#081019', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', marginRight: '12px'}}>2</div>
                <h3 style={{margin: '0', fontSize: '20px'}}>Become an Executive Producer</h3>
              </div>
              <p style={{marginBottom: '20px', lineHeight: '1.5'}}>In addition to your own dedicated evening, you'll get free tickets throughout the run and will be invited to attend production meetings. Your name as exec producer will appear on all posters, fliers, programmes and online materials.</p>
              <a href="mailto:craig@loveyourwork.uk?subject=Executive%20Producer%20enquiry%20—%20While%20We%20Wait&body=Hello,%20I'd%20like%20to%20help%20Support%20British%20Theatre..." className="btn" style={{width: '100%', justifyContent: 'center'}}>Enquire Now</a>
            </article>
          </div>
        </section>

        {/* Actors spotlight */}
        <section className="card cols-2" id="actors">
          <div>
            <img src="/assets/kirsten-callaghan.jpg" alt="Kirsten Callaghan" />
          </div>
          <div>
            <img src="/assets/ricky-oakley.jpg" alt="Ricky Oakley" />
          </div>
          <div className="actors-grid">
            <p className="sub">Starring award‑winning actress <strong>Kirsten Callaghan</strong> and acclaimed actor <strong>Ricky Oakley</strong>.</p>
          </div>
        </section>

        {/* Social proof */}
        <section className="card" id="praise">
          <h2 className="heading-lg">Praise for Doe Wilmann's last play</h2>
          <img src="/assets/praise-for-tpojg.jpg" alt="Praise for The Picture of John Gray — montage of tweets and reviews" />
          <div className="quote"><em>"A remarkably absorbing play."</em> — <strong>Paul Vale, The Stage</strong></div>
          <div className="quote"><em>"Most captivating, transforming piece of theatre I've seen in yonks."</em> — <strong>Patrick Strudwick, The i Paper</strong></div>
          <div className="quote"><em>"Superb masterpiece."</em> — <strong>Lucy Ashe, Author of The Sleeping Beauties</strong></div>
        </section>

        {/* How It Works */}
        <section className="card" id="how-it-works">
          <h2 className="heading-lg">How It Works</h2>
          <p style={{marginBottom: '24px'}}>We identify exceptional plays and get them programmed for professional runs in off-West End theatres. Your support allows these plays to be shown in their best possible light. In return, we dedicate an entire evening to you and give you control of the guest list for that performance. Your name remains attached to the play for its transfer to the West End and beyond.</p>
          <p style={{marginBottom: '24px'}}>We believe theatre should be about excellence. When deciding on which plays to support, we have three criteria:</p>
          
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
            <div style={{display: 'flex', alignItems: 'center', padding: '12px', backgroundColor: 'rgba(110,231,210,.1)', borderRadius: '8px'}}>
              <span style={{fontSize: '24px', marginRight: '12px'}}>📝</span>
              <span style={{fontWeight: '600'}}>Great Script</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', padding: '12px', backgroundColor: 'rgba(110,231,210,.1)', borderRadius: '8px'}}>
              <span style={{fontSize: '24px', marginRight: '12px'}}>⭐</span>
              <span style={{fontWeight: '600'}}>Great Actors</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', padding: '12px', backgroundColor: 'rgba(110,231,210,.1)', borderRadius: '8px'}}>
              <span style={{fontSize: '24px', marginRight: '12px'}}>🚫</span>
              <span style={{fontWeight: '600'}}>No Politics</span>
            </div>
          </div>
        </section>

        {/* Support options */}
        <section className="grid" id="support">
          <h2 className="heading-lg">Support British Theatre today</h2>
          <div className="cols-2">
            <article className="card">
              <h3>Become a Patron</h3>
              <ul className="list">
                <li><span className="tick">✓</span>Guest of honour for your chosen evening.</li>
                <li><span className="tick">✓</span>VIP tickets and cast & crew afterparty for your guests.</li>
                <li><span className="tick">✓</span>Your name in programme & published play text.</li>
                <li><span className="tick">✓</span>Your company as an official sponsor.</li>
              </ul>
              <a className="btn secondary" href="mailto:craig@loveyourwork.uk?subject=Patron%20enquiry%20—%20While%20We%20Wait&body=Hello,%20I'd%20like%20to%20help%20Support%20British%20Theatre...">Enquire Now</a>
            </article>
            <article className="card">
              <h3>Become an Executive Producer</h3>
              <ul className="list">
                <li><span className="tick">✓</span>All the patron benefits plus…</li>
                <li><span className="tick">✓</span>Your name on posters and fliers.</li>
                <li><span className="tick">✓</span>Dedicated page in the programme.</li>
                <li><span className="tick">✓</span>Invitation to production meetings.</li>
              </ul>
              <a className="btn" href="mailto:craig@loveyourwork.uk?subject=Executive%20Producer%20enquiry%20—%20While%20We%20Wait&body=Hello,%20I'd%20like%20to%20help%20Support%20British%20Theatre...">Enquire Now</a>
            </article>
          </div>
        </section>
      </main>

      <footer className="container" aria-label="Footer">
        <p><strong>Produced by</strong> Wait With Me Ltd. <strong>Presented by</strong> Meaningless Problems Presents…</p>
        <p>Contact: <a href="mailto:craig@loveyourwork.uk">craig@loveyourwork.uk</a></p>
      </footer>
    </div>
  );
};

export const WaitWithMePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="wait-with-me">
      <header className="container hero" aria-label="Hero">
        <div className="hero-inner">
          <h1 className="heading-xl">While We Wait</h1>
          <p className="sub">Imagine being the guest of honour at the best new play of 2026.</p>
          <div className="cta">
            <a className="btn" href="mailto:craig@loveyourwork.uk?subject=Patron%20enquiry%20—%20While%20We%20Wait&body=Hello,%20I'd%20like%20to%20help%20Support%20British%20Theatre...">Become a Patron</a>
            <a className="btn secondary" href="mailto:craig@loveyourwork.uk?subject=Executive%20Producer%20enquiry%20—%20While%20We%20Wait&body=Hello,%20I'd%20like%20to%20help%20Support%20British%20Theatre...">Become an Executive Producer</a>
          </div>
        </div>
      </header>

      <main className="container grid" aria-label="Main content">
        {/* About the play */}
        <section className="card" id="about">
          <h2 className="heading-lg">About the play</h2>
          
          <p>A man who is experiencing life in slow motion falls for a woman whose life is speeding by. Can their love survive when time is not on their side? <em>While We Wait</em> is a "hilarious and heart‑breaking" new play. 3rd-28th March 2026, The Old Red Lion Theatre, London</p>
          
          <br />
          
          <p><strong>Cast & creatives:</strong> Starring multi-award-winning actress <em>Kirsten Callaghan</em> and acclaimed actor <em>Ricky Oakley</em>. Directed by West End director <em>Scott Le Crass</em>. Written by <em>Doe Wilmann</em>, creator of the <em>Meaningless Problems</em> podcast.</p>
          
          <p><strong>Produced by</strong> <em>Meaningless Problems Presents…</em> under <em>Wait With Me Ltd</em>.</p>
          
          <p><strong>Supported by...</strong> You</p>
        </section>

        {/* Support options explanation */}
        <section className="grid" id="support-explanation">
          <h2 className="heading-lg">Two ways to support the play</h2>
          
          <div className="cols-2">
            <article className="card">
              <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                <div style={{backgroundColor: 'var(--brand)', color: '#081019', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', marginRight: '12px'}}>1</div>
                <h3 style={{margin: '0', fontSize: '20px'}}>Become a Patron</h3>
              </div>
              <p style={{marginBottom: '20px', lineHeight: '1.5'}}>An evening will be held in your honour, showcasing you and your business as proud supporters of British theatre. After the final bow, the actors will thank you on stage before joining your guests for a private afterparty. Your name will be forever associated with the play.</p>
              <a href="mailto:craig@loveyourwork.uk?subject=Patron%20enquiry%20—%20While%20We%20Wait&body=Hello,%20I'd%20like%20to%20help%20Support%20British%20Theatre..." className="btn" style={{width: '100%', justifyContent: 'center'}}>Enquire Now</a>
            </article>

            <article className="card">
              <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                <div style={{backgroundColor: 'var(--brand)', color: '#081019', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', marginRight: '12px'}}>2</div>
                <h3 style={{margin: '0', fontSize: '20px'}}>Become an Executive Producer</h3>
              </div>
              <p style={{marginBottom: '20px', lineHeight: '1.5'}}>In addition to your own dedicated evening, you'll get free tickets throughout the run and will be invited to attend production meetings. Your name as exec producer will appear on all posters, fliers, programmes and online materials.</p>
              <a href="mailto:craig@loveyourwork.uk?subject=Executive%20Producer%20enquiry%20—%20While%20We%20Wait&body=Hello,%20I'd%20like%20to%20help%20Support%20British%20Theatre..." className="btn secondary" style={{width: '100%', justifyContent: 'center'}}>Enquire Now</a>
            </article>
          </div>
        </section>

        {/* Actors spotlight */}
        <section className="card cols-2" id="actors">
          <div>
            <img src="/assets/kirsten-callaghan.jpg" alt="Kirsten Callaghan" />
          </div>
          <div>
            <img src="/assets/ricky-oakley.jpg" alt="Ricky Oakley" />
          </div>
          <div className="actors-grid">
            <p className="sub">Starring award‑winning actress <strong>Kirsten Callaghan</strong> and acclaimed actor <strong>Ricky Oakley</strong>.</p>
          </div>
        </section>

        {/* Social proof */}
        <section className="card" id="praise">
          <h2 className="heading-lg">Praise for Doe Wilmann's last play</h2>
          <img src="/assets/praise-for-tpojg.jpg" alt="Praise for The Picture of John Gray — montage of tweets and reviews" />
          <div className="quote"><em>"A remarkably absorbing play."</em> — <strong>Paul Vale, The Stage</strong></div>
          <div className="quote"><em>"Most captivating, transforming piece of theatre I've seen in yonks."</em> — <strong>Patrick Strudwick, The i Paper</strong></div>
          <div className="quote"><em>"Superb masterpiece."</em> — <strong>Lucy Ashe, Author of The Sleeping Beauties</strong></div>
        </section>

        {/* Support options */}
        <section className="grid" id="support">
          <h2 className="heading-lg">Support British theatre today</h2>
          <div className="cols-2">
            <article className="card">
              <h3>Become a Patron</h3>
              <ul className="list">
                <li><span className="tick">✓</span>Guest of honour for your chosen evening.</li>
                <li><span className="tick">✓</span>VIP tickets and cast & crew afterparty for your guests.</li>
                <li><span className="tick">✓</span>Your name in programme & published play text.</li>
                <li><span className="tick">✓</span>Your company as an official sponsor.</li>
              </ul>
              <a className="btn" href="mailto:craig@loveyourwork.uk?subject=Patron%20enquiry%20—%20While%20We%20Wait&body=Hello,%20I'd%20like%20to%20help%20Support%20British%20Theatre...">Enquire Now</a>
            </article>
            <article className="card">
              <h3>Become an Executive Producer</h3>
              <ul className="list">
                <li><span className="tick">✓</span>All the patron benefits plus…</li>
                <li><span className="tick">✓</span>Your name on posters and fliers.</li>
                <li><span className="tick">✓</span>Dedicated page in the programme.</li>
                <li><span className="tick">✓</span>Invitation to production meetings.</li>
              </ul>
              <a className="btn secondary" href="mailto:craig@loveyourwork.uk?subject=Executive%20Producer%20enquiry%20—%20While%20We%20Wait&body=Hello,%20I'd%20like%20to%20help%20Support%20British%20Theatre...">Enquire Now</a>
            </article>
          </div>
        </section>
      </main>

      <footer className="container" aria-label="Footer">
        <p><strong>Produced by</strong> Wait With Me Ltd. <strong>Presented by</strong> Meaningless Problems Presents…</p>
        <p>Contact: <a href="mailto:craig@loveyourwork.uk">craig@loveyourwork.uk</a></p>
      </footer>
    </div>
  );
};

export const WorkshopBookingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    workshop: '',
    name: '',
    email: '',
    company: '',
    phone: '',
    participants: '',
    idealDate: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submit-workshop-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit workshop booking');
      }

      const result = await response.json();
      console.log('Workshop booking submitted successfully:', result);
      
      alert('Thank you! Your workshop booking request has been sent to Craig. He will contact you within 24 hours to confirm details.');
      
      // Reset form
      setFormData({
        workshop: '',
        name: '',
        email: '',
        company: '',
        phone: '',
        participants: '',
        idealDate: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error submitting workshop booking:', error);
      alert('There was an error submitting your booking request. Please try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const workshops = [
    "Time Management & Flow State",
    "Creative Mindset Training", 
    "AI Integration for Flow",
    "Custom Workshop (TBD)"
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
            Book a workshop
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Tell us about your needs and we'll create the perfect flow state workshop for your team
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6">
              Workshop booking details
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Which workshop are you interested in? *
                </label>
                <select
                  name="workshop"
                  required
                  value={formData.workshop}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Select a workshop</option>
                  {workshops.map((workshop, index) => (
                    <option key={index} value={workshop}>{workshop}</option>
                  ))}
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Participants *
                  </label>
                  <select
                    name="participants"
                    required
                    value={formData.participants}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="">Select number of participants</option>
                    <option value="1-5">1-5 people</option>
                    <option value="6-10">6-10 people</option>
                    <option value="11-15">11-15 people</option>
                    <option value="16-20">16-20 people</option>
                    <option value="21-30">21-30 people</option>
                    <option value="30+">30+ people</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ideal Workshop Date
                  </label>
                  <input
                    type="date"
                    name="idealDate"
                    value={formData.idealDate}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Information
                </label>
                <textarea
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your team, specific goals, or any other requirements..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-teal-600 text-white px-8 py-3 rounded-md font-medium hover:bg-teal-700 transition-colors"
              >
                Submit Workshop Request
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-8">
            What happens next?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-teal-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Review</h3>
              <p className="text-gray-600">We review your requirements and prepare a customised proposal</p>
            </div>
            
            <div className="text-center">
              <div className="bg-teal-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect</h3>
              <p className="text-gray-600">We schedule a call to discuss your specific needs and finalise details</p>
            </div>
            
            <div className="text-center">
              <div className="bg-teal-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Deliver</h3>
              <p className="text-gray-600">We deliver an outstanding workshop tailored to your team's needs</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// FAQs Page
export const FAQsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Content coming soon
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-lg shadow-lg p-12">
            <div className="text-6xl mb-6">❓</div>
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">
              FAQs Coming Soon
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              We're preparing answers to frequently asked questions about our flow state methodology and services.
            </p>
            <p className="text-gray-600 mb-8">
              In the meantime, feel free to book a call with Craig for any questions you might have.
            </p>
            <a 
              href="https://app.usemotion.com/meet/craigwilmann/meeting" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-teal-600 text-white px-6 py-3 rounded-md font-medium hover:bg-teal-700 transition-colors"
            >
              Book a Call
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Testimonials Page
export const TestimonialsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
            Client Testimonials
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Content coming soon
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-lg shadow-lg p-12">
            <div className="text-6xl mb-6">💬</div>
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">
              Testimonials Coming Soon
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              We're gathering testimonials from our clients who have transformed their businesses through our flow state methodology.
            </p>
            <p className="text-gray-600 mb-8">
              Want to be one of our success stories? Book a call with Craig to get started.
            </p>
            <a 
              href="https://app.usemotion.com/meet/craigwilmann/meeting" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-teal-600 text-white px-6 py-3 rounded-md font-medium hover:bg-teal-700 transition-colors"
            >
              Book a Call
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Terms and Conditions Page
export const TermsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
            Terms and Conditions
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Last updated: January 2025
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2>1. Introduction</h2>
            <p>Welcome to loveyourwork.uk ("we," "our," or "us"). These Terms and Conditions ("Terms") govern your use of our website and services. By accessing or using our website, you agree to be bound by these Terms.</p>

            <h2>2. About Us</h2>
            <p>Love Your Work is a business coaching service founded by Craig Wilmann, specializing in helping businesses unlock flow state for improved performance and wellbeing. Our registered address is in the United Kingdom.</p>

            <h2>3. Services</h2>
            <p>We provide business coaching, consulting, and training services including but not limited to:</p>
            <ul>
              <li>Flow state coaching and training</li>
              <li>Business transformation consulting</li>
              <li>Time management workshops</li>
              <li>Leadership development</li>
            </ul>

            <h2>4. Booking and Payment</h2>
            <p>When you book our services:</p>
            <ul>
              <li>All bookings are subject to availability</li>
              <li>Payment terms will be agreed upon booking</li>
              <li>Cancellation policies will be communicated at time of booking</li>
              <li>We reserve the right to modify or cancel services with reasonable notice</li>
            </ul>

            <h2>5. Intellectual Property</h2>
            <p>All content on loveyourwork.uk, including but not limited to text, graphics, logos, and methodologies, is owned by Love Your Work and protected by UK and international copyright laws.</p>

            <h2>6. Privacy</h2>
            <p>Your privacy is important to us. Please review our Privacy Policy, which governs how we collect, use, and protect your personal information.</p>

            <h2>7. Limitation of Liability</h2>
            <p>To the fullest extent permitted by UK law, Love Your Work shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our services.</p>

            <h2>8. Governing Law</h2>
            <p>These Terms are governed by and construed in accordance with the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the English courts.</p>

            <h2>9. Contact Information</h2>
            <p>If you have any questions about these Terms, please contact us at craig@loveyourwork.uk</p>

            <h2>10. Changes to Terms</h2>
            <p>We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on loveyourwork.uk</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Privacy Policy Page
export const PrivacyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-20 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Last updated: January 2025
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2>1. Introduction</h2>
            <p>Love Your Work ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you visit loveyourwork.uk or use our services.</p>

            <h2>2. Information We Collect</h2>
            <h3>Personal Information</h3>
            <p>We may collect the following personal information:</p>
            <ul>
              <li>Name and contact details (email, phone number)</li>
              <li>Company information</li>
              <li>Assessment responses and coaching-related information</li>
              <li>Communication preferences</li>
            </ul>

            <h3>Technical Information</h3>
            <p>We may automatically collect:</p>
            <ul>
              <li>IP address and location data</li>
              <li>Browser type and version</li>
              <li>Website usage patterns</li>
              <li>Device information</li>
            </ul>

            <h2>3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li>Provide our coaching and consulting services</li>
              <li>Process assessment results and create personalized recommendations</li>
              <li>Communicate about our services and appointments</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2>4. Legal Basis (GDPR)</h2>
            <p>Under GDPR, we process your data based on:</p>
            <ul>
              <li>Consent (when you voluntarily provide information)</li>
              <li>Contract performance (to deliver our services)</li>
              <li>Legitimate interests (to improve our services)</li>
            </ul>

            <h2>5. Data Sharing</h2>
            <p>We do not sell or rent your personal information. We may share data with:</p>
            <ul>
              <li>Service providers who assist in delivering our services</li>
              <li>Legal authorities when required by law</li>
              <li>Business partners with your explicit consent</li>
            </ul>

            <h2>6. Data Security</h2>
            <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>

            <h2>7. Your Rights</h2>
            <p>Under UK data protection law, you have the right to:</p>
            <ul>
              <li>Access your personal data</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your data</li>
              <li>Object to processing</li>
              <li>Data portability</li>
              <li>Withdraw consent</li>
            </ul>

            <h2>8. Cookies</h2>
            <p>Our website uses cookies to enhance your experience. You can control cookie preferences through your browser settings.</p>

            <h2>9. Data Retention</h2>
            <p>We retain your personal information only as long as necessary to provide our services and comply with legal obligations.</p>

            <h2>10. Contact Us</h2>
            <p>For any privacy-related questions or to exercise your rights, please contact us at craig@loveyourwork.uk</p>

            <h2>11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy periodically. Changes will be posted on loveyourwork.uk with an updated effective date.</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};